import { ensurePermissionsLoaded, checkButtonPermission, checkSpecialFeature, checkButtonPermissionOnServer } from '@/shared/utils/permissionUtils';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useDialog } from '@/shared/composables/useDialog';
import { useDialogStore } from '@/shared/stores/dialog';

export function usePermissionGuard() {
  const { createSyncT } = useTranslation();
  const languageStore = useLanguageStore();
  const { alert } = useDialog();
  const dialogStore = useDialogStore();

  // Special whitelist mapping (scoped to avoid side effects)
  // Key format: `${menuKey}:${buttonName}` → ACTION_VALUE
  const SPECIAL_GUARD_MAP: Record<string, string> = {
    '/marketplace:Subscribe': 'Marketplace Subscribe',
    '/marketplace:Cancel': 'Marketplace Subscribe', // grouped with Subscribe
  };

  /**
   * 서버 세션 기준으로 버튼 권한을 확인한다.
   * - 클라이언트 sessionStore(/auth/me 복사본)는 변조될 수 있으므로 서버에 다시 묻는다.
   * - 서버 확인 실패(네트워크 등) 시에만 기존 로컬 판단으로 폴백한다.
   *   (실제 실행 API 는 서버에서 다시 권한을 재검증하므로 폴백해도 데이터는 보호됨)
   */
  async function resolveAllowed(menuKey: string, buttonName: string): Promise<boolean> {
    try {
      const allowed = await checkButtonPermissionOnServer(menuKey, buttonName);
      console.log('[PermissionGuard] server permission check result', { menuKey, buttonName, allowed });
      return allowed;
    } catch (e) {
      console.warn('[PermissionGuard] server check failed, fallback to local', { menuKey, buttonName, error: e });
      const perms = await ensurePermissionsLoaded();
      let allowed = checkButtonPermission(perms, menuKey, buttonName);
      if (!allowed && perms && (perms as any).isAuthEnabled !== false) {
        const specialKey = SPECIAL_GUARD_MAP[`${menuKey}:${buttonName}`];
        if (specialKey && checkSpecialFeature(perms as any, specialKey)) {
          allowed = true;
        }
      }
      return allowed;
    }
  }

  async function guardAndRun(menuKey: string, buttonName: string, run: () => void | Promise<void>) {
    console.log('[PermissionGuard] click -> start', { menuKey, buttonName });

    const allowed = await resolveAllowed(menuKey, buttonName);
    console.log('[PermissionGuard] permission check result', { menuKey, buttonName, allowed });

    if (!allowed) {
      const t = createSyncT(languageStore.currentLanguage);

      const raw = t('access.PermissionDenied');
      const missingByKey = raw === 'access.PermissionDenied' || raw === 'access.Permissiondenied';

      const fallback = t('common.denied');
      const msg = !missingByKey
        ? raw
        : (fallback || 'You do not have sufficient permissions. Please contact your manager.');

      console.warn('[PermissionGuard] denied', {
        menuKey,
        buttonName,
        rawMessage: raw,
        missingByKey,
        finalMessage: msg,
      });

      try {
        // 권한 거부 알림은 "큐에만 쌓이고 UI가 안 뜨는" 문제를 방지하기 위해
        // 해당 분기에서 다이얼로그 상태를 초기화 후 즉시 표시합니다.
        dialogStore.resetDialogState();
        await alert({ title: 'Alert', message: msg, type: 'error', persistent: true });
        console.warn('[PermissionGuard] alert() resolved');
      } catch (e) {
        console.error('[PermissionGuard] alert() failed', e);
      }

      return;
    }

    console.log('[PermissionGuard] allowed -> run()');
    await run();
  }

  /**
   * 함수 팩토리 형태로 "권한 체크 + run 실행" 핸들러를 생성합니다.
   * - 예) const onClick = createGuardedAction('/Contact','Delete',(contact)=>handleDelete(contact))
   * - 템플릿에서는 @click="onClick(item)"처럼 그대로 사용합니다.
   */
  function createGuardedAction<TArgs extends any[]>(
    menuKey: string,
    buttonName: string,
    run: (...args: TArgs) => void | Promise<void>
  ) {
    return async (...args: TArgs) => {
      await guardAndRun(menuKey, buttonName, () => run(...args));
    };
  }

  return { guardAndRun, createGuardedAction };
}
